import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useConfig } from "@/hooks/useConfig";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft } from "lucide-react";

export default function LeadQuickAdd() {
  const navigate = useNavigate();
  const { getValues } = useConfig();
  const sources = getValues("lead_sources");
  const buyerTypes = getValues("lead_buyer_types");
  const [channels, setChannels] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", email: "", company: "", pincode: "", address: "", city: "", district: "", state: "", lead_kind: "buyer", source: "walk_in", buyer_type: "", sales_channel_ids: [], internal_notes: "", assign_to: "" });
  const [saving, setSaving] = useState(false);
  const [pinNote, setPinNote] = useState("");
  // PART C (C5): who the lead goes to, chosen while it is being added
  const [agents, setAgents] = useState([]);
  const [canAssignOthers, setCanAssignOthers] = useState(false);

  useEffect(() => {
    api.get("/leads/assignable-agents").then(r => {
      setAgents(r.data?.agents || []);
      setCanAssignOthers(!!r.data?.can_assign_others);
      const me = (r.data?.agents || []).find(a => a.is_me);
      if (me) setForm(f => (f.assign_to ? f : { ...f, assign_to: me.user_id }));
    }).catch(() => {});
  }, []);

  useEffect(() => { api.get("/leads/channels").then(r => setChannels(r.data || [])).catch(() => {}); }, []);

  // LD-IMPORT B9 + DP-FIX-1 E — a valid pincode fills DISTRICT and State. The typed city
  // is never overwritten; with no city typed yet, the district is offered as the city.
  useEffect(() => {
    const pin = (form.pincode || "").trim();
    if (pin.length !== 6 || !/^\d{6}$/.test(pin)) { setPinNote(""); return; }
    let cancelled = false;
    api.get(`/pincode-lookup/${pin}`)
      .then(r => {
        if (cancelled) return;
        setPinNote("");
        setForm(f => ({
          ...f,
          district: r.data.district || f.district,
          state: r.data.state || f.state,
          city: f.city || r.data.district || "",
        }));
      })
      .catch(() => { if (!cancelled) setPinNote("Pincode not found — check it"); });
    return () => { cancelled = true; };
  }, [form.pincode]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleChannel = (id) => setForm(f => ({ ...f, sales_channel_ids: f.sales_channel_ids.includes(id) ? f.sales_channel_ids.filter(x => x !== id) : [...f.sales_channel_ids, id] }));

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const r = await api.post("/leads", form);
      if (r.data.duplicate) {
        toast.warning("This lead already exists — Open it");
        if (r.data.existing_lead_id) navigate(`/leads/${r.data.existing_lead_id}`);
        return;
      }
      toast.success("Lead created");
      navigate(`/leads/${r.data._id}?edit=1`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto" data-testid="lead-quick-add-page">
      <div className="flex items-center gap-3 mb-5">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4" /></Button>
        <h1 className="text-xl font-semibold text-zinc-900">Quick Add Lead</h1>
      </div>
      <div className="space-y-4">
        <div className="border border-zinc-200 rounded-sm p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-zinc-500">Name *</Label><Input value={form.name} onChange={e => set("name", e.target.value)} className="h-8 text-xs mt-1" data-testid="input-name" /></div>
            <div><Label className="text-xs text-zinc-500">Phone</Label><Input value={form.phone} onChange={e => set("phone", e.target.value)} className="h-8 text-xs mt-1" data-testid="input-phone" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-zinc-500">Email</Label><Input value={form.email} onChange={e => set("email", e.target.value)} className="h-8 text-xs mt-1" data-testid="input-email" /></div>
            <div><Label className="text-xs text-zinc-500">Company</Label><Input value={form.company} onChange={e => set("company", e.target.value)} className="h-8 text-xs mt-1" data-testid="input-company" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs text-zinc-500">Pincode</Label><Input value={form.pincode} onChange={e => set("pincode", e.target.value)} className="h-8 text-xs mt-1" maxLength={6} data-testid="input-pincode" />
              {pinNote && <p className="text-[10px] text-amber-600 mt-0.5" data-testid="pincode-warning">{pinNote}</p>}
            </div>
            <div><Label className="text-xs text-zinc-500">Source</Label>
              <Select value={form.source} onValueChange={v => set("source", v)}>
                <SelectTrigger className="h-8 text-xs mt-1" data-testid="select-source"><SelectValue /></SelectTrigger>
                <SelectContent>{sources.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-zinc-500">Lead Kind</Label>
              <Select value={form.lead_kind} onValueChange={v => set("lead_kind", v)}>
                <SelectTrigger className="h-8 text-xs mt-1" data-testid="select-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="buyer">Buyer</SelectItem>
                  <SelectItem value="partner">Partner</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="text-xs text-zinc-500">Address</Label><Input value={form.address} onChange={e => set("address", e.target.value)} className="h-8 text-xs mt-1" data-testid="input-address" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs text-zinc-500">City</Label><Input value={form.city} onChange={e => set("city", e.target.value)} className="h-8 text-xs mt-1" data-testid="input-city" /></div>
            <div><Label className="text-xs text-zinc-500">District</Label><Input value={form.district} onChange={e => set("district", e.target.value)} className="h-8 text-xs mt-1" data-testid="input-district" /></div>
            <div><Label className="text-xs text-zinc-500">State</Label><Input value={form.state} onChange={e => set("state", e.target.value)} className="h-8 text-xs mt-1" data-testid="input-state" /></div>
          </div>
          <div><Label className="text-xs text-zinc-500">Buyer Type</Label>
            <Select value={form.buyer_type} onValueChange={v => set("buyer_type", v)}>
              <SelectTrigger className="h-8 text-xs mt-1" data-testid="select-buyer-type"><SelectValue placeholder="Select buyer type" /></SelectTrigger>
              <SelectContent>{buyerTypes.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs text-zinc-500">Assign to {canAssignOthers ? "" : "(you)"}</Label>
            <Select value={form.assign_to || "none"} onValueChange={v => set("assign_to", v === "none" ? "" : v)}>
              <SelectTrigger className="h-8 text-xs mt-1" data-testid="select-assign-to"><SelectValue placeholder="Leave unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Leave unassigned</SelectItem>
                {agents.map(a => <SelectItem key={a.user_id} value={a.user_id} data-testid={`assign-option-${a.user_id}`}>{a.name}{a.is_me ? " (you)" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            {!canAssignOthers && <p className="text-[10px] text-zinc-400 mt-1">Only a Sales Manager or Admin can hand a lead to somebody else.</p>}
          </div>
          {channels.length > 0 && (
            <div>
              <Label className="text-xs text-zinc-500">Sales Channels</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {channels.map(c => (
                  <button key={c._id} onClick={() => toggleChannel(c._id)} className={`px-2 py-0.5 text-[10px] rounded-sm border ${form.sales_channel_ids.includes(c._id) ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200"}`} data-testid={`channel-${c._id}`}>{c.name}</button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}>Cancel</Button>
          <Button size="sm" onClick={handleSubmit} disabled={saving} data-testid="submit-lead">{saving ? "Saving..." : "Add Lead"}</Button>
        </div>
      </div>
    </div>
  );
}
